import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList } from "recharts";
import { TrendingDown, PiggyBank, Download, Trash2, Plus, Ship } from "lucide-react";
import { toast } from "sonner";

const usd = (n) => `US$${Math.round(Number(n) || 0).toLocaleString()}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

const BarTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border rounded-md px-3 py-2 text-xs shadow-sm">
      <div className="font-medium text-foreground">{payload[0].payload.name}</div>
      <div className="tabular-nums text-muted-foreground">{usd(payload[0].value)} / container</div>
    </div>
  );
};

export default function XenetaVsNac() {
  const [comp, setComp] = useState(null);
  const [error, setError] = useState(false);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [equipment, setEquipment] = useState("40HC");
  const [containers, setContainers] = useState(1);
  const [ledger, setLedger] = useState({ entries: [], total_saving_usd: 0, total_containers: 0, count: 0 });
  const [recording, setRecording] = useState(false);

  const loadLedger = () => api.get("/commercial/xeneta/records").then((r) => setLedger(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/commercial/xeneta").then((r) => {
      setComp(r.data);
      const first = r.data.lanes[0];
      if (first) { setOrigin(first.origin_code); setDestination(first.destination_code); }
    }).catch(() => setError(true));
    loadLedger();
  }, []);

  const lanes = comp?.lanes || [];
  const origins = useMemo(() => {
    const seen = new Set();
    return lanes.filter((l) => (seen.has(l.origin_code) ? false : seen.add(l.origin_code)))
      .map((l) => ({ code: l.origin_code, name: l.origin_name }));
  }, [lanes]);
  const destinations = useMemo(() => {
    const seen = new Set();
    return lanes.filter((l) => l.origin_code === origin)
      .filter((l) => (seen.has(l.destination_code) ? false : seen.add(l.destination_code)))
      .map((l) => ({ code: l.destination_code, name: l.destination_name }));
  }, [lanes, origin]);

  const onOrigin = (v) => {
    setOrigin(v);
    const first = lanes.find((l) => l.origin_code === v);
    if (first) setDestination(first.destination_code);
  };

  const lane = lanes.find((l) => l.origin_code === origin && l.destination_code === destination);
  const size = equipment === "20GP" ? "20" : "40";
  const xeneta = lane ? lane[`xeneta_${size}`] : 0;
  const nac = lane ? lane[`nac_${size}`] : 0;
  const perContainer = lane ? lane[`saving_${size}`] : 0;
  const qty = Math.max(1, parseInt(containers, 10) || 0);
  const totalSaving = perContainer * qty;
  const savingPct = xeneta ? (perContainer / xeneta) * 100 : 0;

  const chartData = [
    { name: "SGL NAC", value: nac },
    { name: "Xeneta spot", value: xeneta },
  ];

  const record = async () => {
    if (!lane) return;
    setRecording(true);
    try {
      await api.post("/commercial/xeneta/record", { origin_code: origin, destination_code: destination, equipment, containers: qty });
      toast.success("Cost avoidance recorded", { description: `${usd(totalSaving)} across ${qty} × ${equipment}` });
      await loadLedger();
    } catch (e) {
      toast.error("Could not record", { description: e.response?.data?.detail || "Please try again." });
    } finally {
      setRecording(false);
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/commercial/xeneta/records/${id}`); await loadLedger(); toast.success("Entry removed"); }
    catch (e) { toast.error("Could not remove", { description: e.response?.data?.detail || "Please try again." }); }
  };

  const exportCsv = () => {
    const head = ["Recorded", "Origin", "Destination", "Equipment", "Containers", "Xeneta USD/ctr", "SGL NAC USD/ctr", "Saving USD/ctr", "Total saving USD", "Recorded by"];
    const rows = ledger.entries.map((e) => [
      fmtDate(e.created_at), `${e.origin_name} (${e.origin_code})`, `${e.destination_name} (${e.destination_code})`,
      e.equipment, e.containers, e.xeneta_usd, e.nac_usd, e.saving_per_container_usd, e.total_saving_usd, e.user_name || e.user_email || "",
    ]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "PACT-cost-avoidance-ledger.csv";
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    toast.success("Ledger exported", { description: "PACT-cost-avoidance-ledger.csv" });
  };

  if (error) return <div className="border border-border bg-white p-10 text-center text-sm text-muted-foreground" data-testid="xeneta-error">Unable to load the Xeneta comparison. Please try again.</div>;
  if (!comp) return <div className="border border-border bg-white p-16 text-center font-mono-plex text-xs tracking-widest uppercase text-muted-foreground animate-pulse" data-testid="xeneta-loading">Loading Xeneta comparison…</div>;

  return (
    <div data-testid="xeneta-vs-nac">
      <p className="text-sm text-muted-foreground max-w-3xl mb-6">
        Cost avoidance from booking on the SGL NAC contract instead of the Xeneta spot market. NAC = SGL Ocean Freight + Fuel Surcharge + Any Other Charges; Xeneta = spot ocean freight incl. BAF, RRI, PCS, PSS &amp; GRI. All figures in USD per container. Index as at {comp.meta.as_of}.
      </p>

      {/* Calculator inputs */}
      <div className="border border-border bg-white p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 items-end" data-testid="xeneta-lookup">
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Origin</label>
          <Select value={origin} onValueChange={onOrigin}>
            <SelectTrigger data-testid="xeneta-origin" className="w-full bg-white border border-border rounded-md"><SelectValue /></SelectTrigger>
            <SelectContent>{origins.map((o) => <SelectItem key={o.code} value={o.code}>{o.name} ({o.code})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Destination</label>
          <Select value={destination} onValueChange={setDestination}>
            <SelectTrigger data-testid="xeneta-destination" className="w-full bg-white border border-border rounded-md"><SelectValue /></SelectTrigger>
            <SelectContent>{destinations.map((d) => <SelectItem key={d.code} value={d.code}>{d.name} ({d.code})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Equipment</label>
          <div className="inline-flex border border-border rounded-md overflow-hidden w-full" data-testid="xeneta-equipment-toggle">
            {["20GP", "40HC"].map((eq) => (
              <button key={eq} onClick={() => setEquipment(eq)} data-testid={`xeneta-equipment-${eq}`}
                className={`flex-1 px-3 py-2 text-xs font-mono-plex tracking-widest uppercase transition-colors ${equipment === eq ? "bg-[#0072B8] text-white" : "bg-white text-muted-foreground hover:text-foreground"}`}>
                {eq}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Containers</label>
          <input type="number" min="1" value={containers} onChange={(e) => setContainers(e.target.value)} data-testid="xeneta-containers"
            className="w-full bg-white border border-border rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-[#0072B8]/30" />
        </div>
      </div>

      {!lane ? (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="xeneta-noresult">No Xeneta comparison on file for this port pair.</div>
      ) : (
        <div data-testid="xeneta-result">
          {/* Hero cost-avoided figure */}
          <div className="border-2 border-[#0072B8] bg-[#0072B8]/[0.04] p-6 mb-8" data-testid="xeneta-hero">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 text-[#0072B8] mb-2"><PiggyBank className="h-4 w-4" /><span className="font-mono-plex text-[11px] tracking-widest uppercase font-semibold">Cost avoided · NAC vs Xeneta spot</span></div>
                <div className="font-display text-5xl tracking-tight text-foreground tabular-nums" data-testid="xeneta-total-saving">{usd(totalSaving)}</div>
                <div className="text-sm text-muted-foreground mt-1">{usd(perContainer)} per container × {qty} × {equipment} · {lane.origin_name} → {lane.destination_name}</div>
              </div>
              <div className="lg:text-right lg:border-l lg:border-[#0072B8]/25 lg:pl-8">
                <div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Saving vs spot</div>
                <div className="flex items-center gap-2 lg:justify-end text-emerald-600"><TrendingDown className="h-6 w-6" /><span className="font-display text-3xl tabular-nums">{savingPct.toFixed(1)}%</span></div>
                <div className="text-[11px] text-muted-foreground mt-1">below the Xeneta spot rate</div>
              </div>
            </div>
            <button onClick={record} disabled={recording || perContainer <= 0} data-testid="xeneta-record-btn"
              className="mt-5 inline-flex items-center gap-2 bg-[#0072B8] text-white text-xs font-mono-plex tracking-widest uppercase px-4 py-2.5 rounded-md hover:bg-[#005a92] transition-colors disabled:opacity-50">
              <Plus className="h-3.5 w-3.5" /> {recording ? "Recording…" : "Record this cost avoidance"}
            </button>
          </div>

          {/* Chart + table */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
            <div className="border border-border bg-white p-6" data-testid="xeneta-chart">
              <div className="flex items-center gap-2 mb-4"><Ship className="h-4 w-4 text-[#0072B8]" strokeWidth={1.75} /><span className="font-mono-plex text-[11px] tracking-widest uppercase font-semibold">Rate per {equipment} (US$)</span></div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="name" stroke="#64748B" fontSize={12} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                  <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                  <Tooltip content={<BarTip />} cursor={{ fill: "#0072B8", fillOpacity: 0.04 }} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={90}>
                    <Cell fill="#0072B8" />
                    <Cell fill="#F59E0B" />
                    <LabelList dataKey="value" position="top" formatter={(v) => usd(v)} style={{ fontSize: 11, fill: "#334155", fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="text-[11px] text-muted-foreground/70 mt-2">Blue = SGL NAC contract · Amber = Xeneta spot. The gap is your cost avoidance.</p>
            </div>

            <div className="border border-border bg-white overflow-x-auto" data-testid="xeneta-table">
              <div className="flex items-center gap-2 px-5 py-3.5 border-b border-border bg-slate-50/70">
                <span className="font-mono-plex text-[11px] tracking-widest uppercase font-semibold">Comparison · {equipment}</span>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-slate-100"><td className="px-5 py-3 text-muted-foreground">Xeneta spot rate</td><td className="px-5 py-3 text-right tabular-nums font-semibold text-[#F59E0B]">{usd(xeneta)}</td></tr>
                  <tr className="border-b border-slate-100"><td className="px-5 py-3 text-muted-foreground">SGL NAC rate</td><td className="px-5 py-3 text-right tabular-nums font-semibold text-[#0072B8]">{usd(nac)}</td></tr>
                  <tr className="border-b border-slate-100"><td className="px-5 py-3 text-foreground font-medium">Saving per container</td><td className="px-5 py-3 text-right tabular-nums font-semibold text-emerald-600">{usd(perContainer)}</td></tr>
                  <tr className="border-b border-slate-100"><td className="px-5 py-3 text-muted-foreground">Containers</td><td className="px-5 py-3 text-right tabular-nums">{qty}</td></tr>
                  <tr className="bg-[#0072B8]/[0.05]"><td className="px-5 py-3.5 font-mono-plex text-[11px] tracking-widest uppercase font-semibold">Total cost avoided</td><td className="px-5 py-3.5 text-right tabular-nums font-display text-xl text-foreground">{usd(totalSaving)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Recorded cost-avoidance ledger */}
      <div className="mb-2 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-2xl tracking-tight">Recorded cost avoidance</h3>
          <p className="text-xs text-muted-foreground mt-1">Program-wide ledger · {ledger.count} {ledger.count === 1 ? "entry" : "entries"} · {ledger.total_containers} containers</p>
        </div>
        <button onClick={exportCsv} disabled={!ledger.entries.length} data-testid="xeneta-export-csv"
          className="inline-flex items-center gap-2 border border-border rounded-md px-3 py-1.5 text-[11px] font-mono-plex tracking-widest uppercase text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground transition-colors disabled:opacity-40">
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
      </div>

      <div className="border-2 border-emerald-500/40 bg-emerald-50/40 p-5 mb-6 flex items-center justify-between" data-testid="xeneta-total-recorded">
        <div className="font-mono-plex text-[11px] tracking-widest uppercase text-emerald-700">Total recorded to date</div>
        <div className="font-display text-3xl tracking-tight text-emerald-700 tabular-nums" data-testid="xeneta-total-recorded-value">{usd(ledger.total_saving_usd)}</div>
      </div>

      <div className="border border-border bg-white overflow-x-auto" data-testid="xeneta-ledger">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border bg-slate-50/70">
              {["Recorded", "Lane", "Equip", "Ctrs", "Saving / ctr", "Total saving", "By", ""].map((h, i) => (
                <th key={i} className="px-4 py-2.5 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ledger.entries.map((e) => (
              <tr key={e.id} className="border-t border-slate-100" data-testid={`xeneta-ledger-row-${e.id}`}>
                <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(e.created_at)}</td>
                <td className="px-4 py-2.5 text-foreground/90 whitespace-nowrap">{e.origin_code} → {e.destination_code}</td>
                <td className="px-4 py-2.5 text-xs">{e.equipment}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{e.containers}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{usd(e.saving_per_container_usd)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-emerald-600">{usd(e.total_saving_usd)}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{e.user_name || e.user_email}</td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => remove(e.id)} data-testid={`xeneta-ledger-delete-${e.id}`} className="text-muted-foreground/60 hover:text-red-600 transition-colors" aria-label="Remove entry">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {!ledger.entries.length && <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">No cost avoidance recorded yet. Use the calculator above and click “Record this cost avoidance”.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
